import React, { useState, useEffect, useMemo, useRef } from "react";
import TerritoryPicker from "./TerritoryPicker";

// ══════════════════════════════════════════════════════════════════════════════
// SolarLead Today - v4 desktop density
//
// May 12 2026: top bar with territory selector + search + live pulse,
// greeting with stats row, magic moment hero with internal split
// (number left, top-3 closest list right), live activity ticker on right rail,
// 2-up plays row, system pulse footer. Outfit Black, brutalist editorial, light.
//
// Customer journey: orient (1s) → flex (3s) → dispatch (10s) → action (30s)
// Powered by GET /api/dashboard/today (snapshot architecture, sub-100ms).
//
// BACKEND TODOs:
//   - briefing.magicMoment   → personalized hero ({count, cohortLabel, estValueDisplay, reasoning, ctaLabel, ctaTargetView})
//   - briefing.closestList   → top 3 candidate rows for the magic-moment cohort within distance
//   - briefing.liveTicker    → last ~6 fresh permits with timestamps (could compute from queue.hot[0].newestPermit)
//   - briefing.totalsForTerritory → { permitsActive, contractorsActive, citiesTracked }
//
// Component falls back gracefully to placeholders + briefing.summary data
// so the UI always renders meaningfully even before backend ships these fields.
// ══════════════════════════════════════════════════════════════════════════════

// ── Types ──
type HotPermit = {
  id?: string | number;
  address: string | null;
  contractor: string | null;
  permitDate: string | null;
  createdMs?: number;
  value: number;
};
type HotItem = {
  city: string; province: string;
  count24h: number;
  count30d: number;
  highValue30d: number;
  newestPermit: HotPermit | null;
};
type TrendingItem = {
  city: string; province: string;
  count30d: number; count24h: number; highValue30d: number;
};
type Queue = { hot: HotItem[]; trending: TrendingItem[] };

type AIBrief = {
  headline: string; why: string; cta: string;
  entityType: 'contractor' | 'city' | 'none' | string;
  entityName: string;
};

type DefendSignal = {
  type: 'contractor_erosion';
  contractor: string; province: string;
  sharePctChange: number;
  priorCount: number; currentCount: number;
  headline: string;
};
type GrowSignal = {
  type: 'rising_market';
  city: string; province: string;
  growthPct: number; newThisMonth: number; total: number;
  headline: string;
};
type HeroData = {
  strategy?: 'new_entry' | 'surge' | 'market';
  newCity: string; newState: string; detectedAt: string;
  contractor?: string;
  newPermitCount?: number; priorTotal?: number;
  priorStates?: string[]; marketGrowthPct?: number;
};

// Lifecycle plays - the upsell-positioning anchors. Each represents a
// population of installed homeowners in a specific motion window. Backend
// generates these in regenerateTodayBriefing() (cron-cached for All Markets,
// recomputed live for territory filters) plus pipelineAging at request time.
type UpsellWindowSignal = {
  type: 'upsell_window';
  count: number; province: string | null; yearRange: string;
  segment?: string;  // frontend SEGMENTS key - used by card CTA to route
  headline: string;
};
type ServiceDueSignal = {
  type: 'service_due';
  count: number; province: string | null; yearRange: string;
  segment?: string;  // frontend SEGMENTS key - used by card CTA to route
  headline: string;
};
type PipelineAgingSignal = {
  type: 'pipeline_aging';
  count: number; oldestDays: number | null;
  headline: string;
};
type Lifecycle = {
  upsellWindow:  UpsellWindowSignal  | null;
  serviceDue:    ServiceDueSignal    | null;
  pipelineAging: PipelineAgingSignal | null;
};

type ClosestCandidate = {
  address: string; city: string;
  distanceMi: number;
  systemKw: number;
  installYear: number;
};

type MagicMoment = {
  count: number;
  cohortLabel: string;
  estValueDisplay: string;
  reasoning: string;
  ctaLabel: string;
  closestList?: ClosestCandidate[];
};

type TickerItem = {
  ts: string;
  address: string; city: string;
  systemKw?: number;
  contractor: string;
  value?: number;
  fresh?: boolean;
};

type Briefing = {
  success: boolean;
  generatedAt: string;
  generationMs?: number;
  warming?: boolean;
  message?: string;
  aiBrief: AIBrief | null;
  queue: Queue;
  defend: DefendSignal | null;
  grow: GrowSignal | null;
  hero: HeroData | null;
  lifecycle?: Lifecycle | null;
  pipelineTotal?: number;  // total active leads in user's pipeline (per-user)
  meta: { citiesTracked: number; overnightPermits: number };
  summary: {
    totalFresh24h: number;
    clustersInHot: number;
    clustersInTrending: number;
    totalLeads: number;
  };
  magicMoment?: MagicMoment | null;
  liveTicker?: TickerItem[] | null;
  totalsForTerritory?: { permitsActive: number; contractorsActive: number; citiesTracked: number; addedThisWeek?: number } | null;
};

type Props = {
  apiBase?: string;
  userFirstName?: string;
  territory?: string;
  onChangeTerritory?: (name: string) => void;
  onViewPermits?: (city: string, province: string) => void;
  onViewPermitsByMotion?: (motion: string, territory?: string) => void;  // routes to filtered Permits view; territory syncs explore filter
  onViewPipeline?: () => void;                        // routes to Pipeline view
  onViewContractor?: (contractor: string) => void;
  onOpenContractors?: () => void;                     // routes to Contractors list view (not detail)
  onAskFollowUp?: (initialPrompt?: string) => void;
  onOpenMap?: () => void;
  onOpenLead?: (address: string, city: string) => void;  // pops the lead drawer for a specific permit (used by live ticker)
  lifecycleSlot?: React.ReactNode;                       // the LifecycleBoard, injected by Dashboard so Today renders as one page
};

// ── Design tokens (light, brutalist editorial) ──
const T = {
  bg: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceRaised: "#FAFAF7",
  surfaceMagic: "#FFF7ED",
  text: "#0A0A0A",
  textSub: "#262B37",
  textMid: "#1A1D26",
  primary: "#FF8800",
  primaryDark: "#E07700",
  defend: "#EF4444",
  grow: "#15803D",
  border: "#E5E5E0",
  borderSoft: "#F0F0EB",
  borderHairline: "#EBEBE5",
  magicAccent: "rgba(255,136,0,0.25)",
};

const FONT_DISPLAY = "'Outfit', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas, monospace";

// ── Helpers ──
const fmtRelativeTime = (iso: string): string => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const fmtClockTime = (d: Date): string => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

const fmtPermitTime = (iso: string | null | undefined, fallback: string): string => {
  if (!iso) return fallback;
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return fallback;
    return fmtClockTime(d);
  } catch {
    return fallback;
  }
};

const fmtCount = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return "0";
  return n.toLocaleString("en-US");
};

const greetingForHour = (h: number): string => {
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 22) return "Good evening";
  return "Working late";
};

// Parse **double-asterisk** highlights from the AI brief HEADLINE into JSX
// nodes. The backend prompt instructs Haiku to wrap 1-2 key phrases in ** for
// orange emphasis. Returns an array of React nodes safe to spread into JSX.
// Plain text passes through unchanged.
function renderWithHighlights(text: string): React.ReactNode[] {
  if (!text) return [];
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={i} style={{ color: T.primary }}>
          {part.slice(2, -2)}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// ── Ticker fallback from queue ──
function computeTicker(briefing: Briefing | null): TickerItem[] {
  if (briefing?.liveTicker?.length) return briefing.liveTicker;
  if (!briefing?.queue?.hot?.length) return [];
  const items: TickerItem[] = [];
  const hot = briefing.queue.hot;
  for (let i = 0; i < Math.min(hot.length, 5); i++) {
    const p = hot[i].newestPermit;
    if (!p || !p.address) continue;
    items.push({
      ts: fmtPermitTime(p.permitDate, ""),
      address: p.address,
      city: hot[i].city,
      contractor: (p.contractor && p.contractor !== "-") ? p.contractor : "",
      value: p.value || 0,
      fresh: i === 0,
    });
  }
  return items;
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function IntelligenceBriefing({
  apiBase,
  userFirstName,
  territory,
  onChangeTerritory,
  onViewPermits,
  onViewPermitsByMotion,
  onViewPipeline,
  onViewContractor,
  onOpenContractors,
  onAskFollowUp,
  onOpenMap,
  onOpenLead,
  lifecycleSlot,
}: Props) {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Two layout breakpoints: compact below 1500px, single-column mobile below 1024.
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);
  const [isCompact, setIsCompact] = useState(typeof window !== 'undefined' && window.innerWidth < 1500);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorPos, setAnchorPos] = useState<{ top: number; left: number } | null>(null);
  const territoryButtonRef = useRef<HTMLButtonElement | null>(null);

  const base = apiBase || "https://solarlead-backend-production.up.railway.app";

  useEffect(() => {
    const h = () => {
      setIsMobile(window.innerWidth < 1024);
      setIsCompact(window.innerWidth < 1500);
    };
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tParam = territory && territory !== 'All Markets' ? territory : 'all';
    const CACHE_KEY = `sl_today_cache_v4_${tParam.replace(/\s+/g, '_')}`;
    const TTL_MS = 30 * 60 * 1000;
    let hasCached = false;

    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.ts && (Date.now() - parsed.ts) < TTL_MS && parsed.data?.success) {
          setBriefing(parsed.data);
          setLoading(false);
          hasCached = true;
        }
      }
    } catch {}

    setError(null);
    setLoading(!hasCached);
    const url = tParam === 'all'
      ? `${base}/api/dashboard/today`
      : `${base}/api/dashboard/today?territory=${encodeURIComponent(tParam)}`;
    fetch(url)
      .then(r => r.json())
      .then((data: Briefing) => {
        if (cancelled) return;
        if (!data || !data.success) {
          if (!hasCached) setError("Briefing unavailable");
        } else {
          setBriefing(data);
          setError(null);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        if (!hasCached) { setError("Could not load briefing"); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [base, territory]);


  // ── Loading state ──
  if (loading && !briefing) {
    return (
      <div style={{ width: "100%" }}>
        <div style={{ padding: isMobile ? "28px 18px" : "32px 28px", maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ height: 12, width: 180, background: T.borderHairline, marginBottom: 16 }} />
          <div style={{ height: 36, width: "60%", background: T.borderHairline, marginBottom: 28 }} />
          <div style={{ height: 280, background: T.borderHairline, marginBottom: 18 }} />
          <div style={{ height: 110, background: T.borderHairline }} />
        </div>
      </div>
    );
  }

  if (briefing?.warming) {
    return (
      <div style={{ width: "100%", padding: isMobile ? "48px 18px" : "80px 32px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", padding: "5px 12px", background: T.surfaceMagic, marginBottom: 18 }}>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.primary, fontWeight: 700, letterSpacing: "0.18em" }}>● BRIEFING WARMING UP</span>
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 900, color: T.text, letterSpacing: "-0.035em", lineHeight: 1, marginBottom: 12 }}>
            Generating your briefing.
          </h2>
          <p style={{ fontSize: 14, color: T.textSub, fontWeight: 500, lineHeight: 1.5 }}>
            {briefing.message || "Fresh intelligence loading. Refresh in a moment."}
          </p>
        </div>
      </div>
    );
  }

  if (error && !briefing) {
    return (
      <div style={{ width: "100%", padding: isMobile ? "48px 18px" : "80px 32px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-block", padding: "5px 12px", background: "rgba(239,68,68,0.08)", marginBottom: 18 }}>
            <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.defend, fontWeight: 700, letterSpacing: "0.18em" }}>● BRIEFING UNAVAILABLE</span>
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 900, color: T.text, letterSpacing: "-0.035em", lineHeight: 1, marginBottom: 12 }}>
            Could not reach the briefing.
          </h2>
          <p style={{ fontSize: 14, color: T.textSub, fontWeight: 500, lineHeight: 1.5 }}>Try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // ── Derived data ──
  const defendSignal = briefing?.defend || null;
  const growSignal = briefing?.grow || null;
  const freshCount = briefing?.summary?.totalFresh24h || 0;
  const totalLeads = briefing?.summary?.totalLeads || 0;
  const citiesTracked = briefing?.meta?.citiesTracked || 0;
  const ticker = computeTicker(briefing);

  // Lifecycle signals - three new plays anchored on the upsell-first
  // positioning. upsellWindow + serviceDue are cron-cached (and recomputed
  // live when a territory is selected). pipelineAging is per-user, merged
  // into the response at request time.
  const lifecycle = briefing?.lifecycle || null;
  const upsellSignal = lifecycle?.upsellWindow || null;

  const tt = briefing?.totalsForTerritory;
  // Real values only - these come from backend territory totals (tt) or the
  // briefing summary. No hardcoded platform numbers: if the backend hasn't
  // supplied a value, it stays null and renders as "-" (never a fabricated total).
  const totalPermits = tt?.permitsActive || totalLeads || null;
  const totalContractors = tt?.contractorsActive || null;
  const totalCities = tt?.citiesTracked || citiesTracked || null;
  // Real "added this week" (created_at) from backend. null = unknown → show no arrow (never fabricate).
  const addedThisWeek = (tt && typeof tt.addedThisWeek === "number") ? tt.addedThisWeek : null;

  const territoryName = (territory || "California").trim();

  const handleFreshCTA = () => {
    if (onViewPermitsByMotion) { onViewPermitsByMotion("installers", territoryName); return; }
    const topHot = briefing?.queue?.hot?.[0];
    if (topHot && onViewPermits) onViewPermits(topHot.city, topHot.province);
    else if (onAskFollowUp) onAskFollowUp("What are the freshest permits in my territory?");
  };
  const handleTerritory = () => {
    if (territoryButtonRef.current) {
      const rect = territoryButtonRef.current.getBoundingClientRect();
      setAnchorPos({ top: rect.bottom + 6, left: rect.left });
    }
    setPickerOpen(true);
  };

  // Helper: format a count nicely (1247 → "1,247", 920000 → "920K")
  const fmtBig = (n: number): string => {
    if (n == null || !Number.isFinite(n)) return "-";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 100_000) return `${Math.round(n / 1000)}K`;
    if (n >= 10_000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  // Color tokens for the new color system. Used inline below.
  const C = {
    orange: "#FF8800",
    green: "#16A34A",
    border: "#EAEAE2",
    borderSoft: "#F4F4EE",
    bgOff: "#FAFAF6",
  };

  // Honest deltas. Permits arrow = real "added this week" (created_at, from backend);
  // daily ingest = that weekly number ÷ 7. All other per-week / per-month / per-quarter
  // deltas are omitted until the backend supplies real period-over-period counts -
  // we never derive a "growth" number from a percentage of the total.
  const dailyAvg = addedThisWeek != null ? Math.max(1, Math.round(addedThisWeek / 7)) : null;

  return (
    <div style={{ width: "100%", minWidth: 0, background: T.bg, minHeight: "100%" }}>
      <div style={{ width: "100%", padding: isMobile ? "20px 18px 40px" : "32px 40px 60px" }}>

        {/* GREETING ROW (compact) */}
        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "flex-start",
          justifyContent: "space-between",
          gap: isMobile ? 12 : 24, marginBottom: 14,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontFamily: FONT_DISPLAY, fontSize: isMobile ? 28 : 42, fontWeight: 900,
              color: T.text, letterSpacing: "-0.04em", lineHeight: 0.98, margin: 0,
            }}>
              {userFirstName ? `${greetingForHour(new Date().getHours())}, ${userFirstName}.` : `${greetingForHour(new Date().getHours())}.`}
            </h1>
          </div>
          <button
            ref={territoryButtonRef}
            onClick={handleTerritory}
            style={{
              flexShrink: 0,
              alignSelf: isMobile ? "flex-start" : "auto",
              display: "inline-flex", alignItems: "center", gap: 10,
              background: T.surface, border: `1.5px solid ${T.text}`,
              padding: "9px 14px", cursor: "pointer",
              fontFamily: FONT_DISPLAY,
              transition: "background 120ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = T.surfaceMagic)}
            onMouseLeave={e => (e.currentTarget.style.background = T.surface)}
          >
            <span style={{
              fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.16em",
              color: T.textMid, fontWeight: 700, textTransform: "uppercase",
            }}>
              Territory
            </span>
            <span style={{ fontSize: 14, fontWeight: 900, color: T.text, letterSpacing: "-0.01em" }}>
              {territoryName}
            </span>
            <span style={{ fontSize: 12, color: T.text, fontWeight: 900, marginLeft: 2 }}>▾</span>
          </button>
        </div>

        {/* STAT TICKER (compact, replaces the bordered ribbon) */}
        {(() => {
          const bits: React.ReactNode[] = [];
          if (totalPermits != null) bits.push(<span key="p"><b style={{ fontWeight: 800, color: "#FF8800" }}>{fmtBig(totalPermits)}{totalPermits >= 100_000 ? "+" : ""}</b> permits</span>);
          if (totalCities != null) bits.push(<span key="m"><b style={{ fontWeight: 800, color: "#FF8800" }}>{totalCities.toLocaleString()}</b> markets</span>);
          if (totalContractors != null) bits.push(<span key="c"><b style={{ fontWeight: 800, color: "#FF8800" }}>{totalContractors.toLocaleString()}</b> contractors</span>);
          if (bits.length === 0) return null;
          return (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 12px", marginBottom: 22, fontFamily: FONT_MONO, fontSize: 12.5, fontWeight: 600, color: T.text, letterSpacing: "0.01em" }}>
              {bits.map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ color: "#262B37" }}>{"\u00b7"}</span>}
                  {b}
                </React.Fragment>
              ))}
              {addedThisWeek != null && <span style={{ color: C.green, fontWeight: 700, marginLeft: 4 }}>{"\u2191"}{fmtBig(addedThisWeek)}/wk</span>}
            </div>
          );
        })()}

        {/* lifecycle board - the single opportunity module, injected by Dashboard */}
        {lifecycleSlot}

        {/* ===== MARKET WIRE ===== */}
        {(() => {
          const wclean = (x: any) => String(x || "").split("**").join("").replace(new RegExp("[\\u2014\\u2013]", "g"), "-").trim();
          const hero = briefing?.hero || null;
          const svc = briefing?.lifecycle?.serviceDue || null;
          const items: { cat: string; color: string; headline: string; meta: string; onClick: () => void }[] = [];

          if (hero && hero.newCity) {
            const place = `${hero.newCity}${hero.newState ? ", " + hero.newState : ""}`;
            if (hero.strategy === "new_entry") {
              items.push({ cat: "New entrant", color: "#16A34A",
                headline: hero.contractor ? `${hero.contractor} just pulled its first permit in ${place}.` : `${place} just logged its first tracked permits.`,
                meta: "Market move",
                onClick: () => { if (hero.contractor && onViewContractor) onViewContractor(hero.contractor); else if (onViewPermits) onViewPermits(hero.newCity, hero.newState || ""); } });
            } else if (hero.strategy === "surge") {
              items.push({ cat: "Contractor surge", color: "#FF8800",
                headline: hero.contractor ? `${hero.contractor} filed ${hero.newPermitCount || "new"} permits in ${place}, well above their pace.` : `Permit activity is surging in ${place}.`,
                meta: "Contractor signal",
                onClick: () => { if (hero.contractor && onViewContractor) onViewContractor(hero.contractor); else if (onViewPermits) onViewPermits(hero.newCity, hero.newState || ""); } });
            } else {
              items.push({ cat: "Market move", color: "#FF8800",
                headline: hero.marketGrowthPct ? `${place} is moving, up ${hero.marketGrowthPct}% on recent permit volume.` : `${place} is showing fresh permit movement.`,
                meta: "Market shift",
                onClick: () => { if (onViewPermits) onViewPermits(hero.newCity, hero.newState || ""); } });
            }
          }

          if (growSignal && growSignal.newThisMonth > 0 && (!hero || (hero.newCity || "").toLowerCase() !== (growSignal.city || "").toLowerCase())) {
            items.push({ cat: "Heating up", color: "#FF8800",
              headline: wclean(growSignal.headline) || `${growSignal.city}, ${growSignal.province} is up ${growSignal.growthPct}% in 30 days, ${growSignal.newThisMonth} new permits.`,
              meta: "Market shift",
              onClick: () => { if (onViewPermits) onViewPermits(growSignal.city, growSignal.province); } });
          }

          (ticker || []).slice(0, 2).forEach((t) => {
            if (!t.address) return;
            const cityName = (t.city || "").split(",")[0].trim();
            items.push({ cat: "Fresh permit", color: "#FF8800",
              headline: t.address,
              meta: `${t.city || ""}${t.contractor ? " \u00b7 " + (t.contractor.length > 24 ? t.contractor.slice(0, 24) + "\u2026" : t.contractor) : ""} \u00b7 ${t.fresh ? "just now" : fmtRelativeTime(t.ts)}`,
              onClick: () => { if (onOpenLead) onOpenLead(t.address, t.city || cityName); else if (onViewPermits) onViewPermits(cityName, ""); } });
          });

          if (defendSignal && defendSignal.currentCount > 0) {
            items.push({ cat: "Share shift", color: "#F43F7E",
              headline: wclean(defendSignal.headline) || `${defendSignal.contractor} share in ${defendSignal.province} slipped ${Math.abs(defendSignal.sharePctChange)}%, territory opening up.`,
              meta: "Competitor signal",
              onClick: () => { if (onViewContractor) onViewContractor(defendSignal.contractor); } });
          }

          if (upsellSignal && upsellSignal.count > 0) {
            items.push({ cat: "Battery window", color: "#2970FF",
              headline: wclean(upsellSignal.headline) || `${upsellSignal.count.toLocaleString()} systems crossed into the ${upsellSignal.yearRange} upsell window.`,
              meta: "Segment",
              onClick: () => { if (onViewPermitsByMotion) onViewPermitsByMotion(upsellSignal.segment || "battery", territoryName); } });
          }
          if (svc && svc.count > 0) {
            items.push({ cat: "Service due", color: "#0B8A87",
              headline: wclean(svc.headline) || `${svc.count.toLocaleString()} systems are in the ${svc.yearRange} service window.`,
              meta: "Segment",
              onClick: () => { if (onViewPermitsByMotion) onViewPermitsByMotion(svc.segment || "cleaning", territoryName); } });
          }

          const wire = items.slice(0, 6);

          return (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FONT_MONO, fontSize: 13, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: T.text }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#FF8800" }} />
                  Market Wire
                </span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.text }}>
                  Live {"\u00b7"} Updated {briefing?.generatedAt ? fmtRelativeTime(briefing.generatedAt) : "now"}
                </span>
              </div>
              {freshCount > 0 && (
                <div onClick={() => handleFreshCTA()} style={{ fontFamily: FONT_MONO, fontSize: 11.5, fontWeight: 700, color: T.text, marginBottom: 14, cursor: "pointer" }}>
                  {"\u25b8"} <span style={{ color: "#FF8800", fontWeight: 800 }}>{freshCount}</span> fresh {freshCount === 1 ? "permit" : "permits"} in {territoryName} today
                </div>
              )}
              {wire.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {wire.map((it, i) => (
                    <div key={i} onClick={it.onClick}
                      style={{ background: T.surface, border: `1.5px solid ${C.border}`, borderLeft: `3px solid ${it.color}`, borderRadius: 14, padding: "13px 15px", cursor: "pointer", transition: "transform 120ms ease, box-shadow 120ms ease" }}
                      onMouseEnter={(e: any) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.06)"; }}
                      onMouseLeave={(e: any) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
                      <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: it.color, marginBottom: 5 }}>{it.cat}</div>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 14.5, fontWeight: 800, color: T.text, letterSpacing: "-0.01em", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{it.headline}</div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 9, gap: 10 }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{it.meta}</span>
                        <span style={{ fontFamily: FONT_DISPLAY, fontSize: 12.5, fontWeight: 800, color: "#FF8800", whiteSpace: "nowrap" }}>View {"\u2192"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: T.surface, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: "28px 18px", textAlign: "center", fontFamily: FONT_MONO, fontSize: 12, color: T.text }}>
                  No market movement in the last 24h.
                </div>
              )}
            </div>
          );
        })()}

      </div>

      {/* ────────────────── TERRITORY PICKER ────────────────── */}
      <TerritoryPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(name) => {
          setPickerOpen(false);
          if (onChangeTerritory) onChangeTerritory(name);
        }}
        currentTerritory={territoryName}
        apiBase={base}
        anchorPos={anchorPos || undefined}
      />

    </div>
  );
}
